import {
  AuthenticationDetails,
  CognitoUser,
  CognitoUserAttribute,
  CognitoUserPool,
} from "amazon-cognito-identity-js";
import { CLIENT_ID, USER_POOL_ID } from "./cognitoConfig";
import { jwtDecode } from "jwt-decode";

const userPool = USER_POOL_ID && CLIENT_ID ? new CognitoUserPool({
  UserPoolId: USER_POOL_ID,
  ClientId: CLIENT_ID,
}) : null;

const getUser = (email) => new CognitoUser({ Username: email, Pool: userPool });

export function refreshAuthToken() {
  if (!userPool) return Promise.reject(new Error("Cognito configuration is required"));
  const currentUser = userPool.getCurrentUser();
  if (!currentUser) {
    return Promise.reject(new Error("No current user"));
  }

  return new Promise((resolve, reject) => {
    currentUser.getSession((err, session) => {
      if (err) {
        reject(err);
        return;
      }

      const refreshToken = session.getRefreshToken();
      if (!refreshToken) {
        reject(new Error("No refresh token available"));
        return;
      }

      currentUser.refreshSession(refreshToken, (err, newSession) => {
        if (err) {
          reject(err);
          return;
        }

        const newToken = newSession.getAccessToken().getJwtToken();
        localStorage.setItem("mg_token", newToken);
        resolve(newToken);
      });
    });
  });
}

export function getCurrentSession() {
  if (!userPool) return Promise.resolve(null);
  const current = userPool.getCurrentUser();
  if (!current) {
    return Promise.resolve(null);
  }

  return new Promise((resolve, reject) => {
    current.getSession((err, session) => {
      if (err) {
        reject(err);
        return;
      }

      if (!session.isValid()) {
        resolve(null);
        return;
      }

      const accessToken = session.getAccessToken();
      const jwtToken = accessToken.getJwtToken();
      const decoded = jwtDecode(jwtToken);
      const currentTime = Math.floor(Date.now() / 1000);
      const timeUntilExpiry = decoded.exp - currentTime;

      if (timeUntilExpiry < 300) {
        const refreshToken = session.getRefreshToken();
        current.refreshSession(refreshToken, (err, newSession) => {
          if (err) {
            console.warn("Failed to refresh token:", err);
            resolve({ user: current, session });
            return;
          }

          const newToken = newSession.getAccessToken().getJwtToken();
          localStorage.setItem("mg_token", newToken);
          resolve({ user: current, session: newSession });
        });
      } else {
        resolve({ user: current, session });
      }
    });
  });
}

// ---------- Sign up ----------
export function signUp({ email, password, name, company, attributes }) {
  // Accept either flat props or an attributes object; always emit custom:Company
  const attrsObj =
    attributes && typeof attributes === "object"
      ? attributes
      : { name: name || "", "custom:Company": company || "" };

  // Normalize both key spellings to custom:Company
  if (attrsObj["custom:company"] && !attrsObj["custom:Company"]) {
    attrsObj["custom:Company"] = attrsObj["custom:company"];
    delete attrsObj["custom:company"];
  }

  const attrs = [
    new CognitoUserAttribute({ Name: "email", Value: email }),
    ...(attrsObj.name
      ? [new CognitoUserAttribute({ Name: "name", Value: attrsObj.name })]
      : []),
    ...(attrsObj["custom:Company"]
      ? [
          new CognitoUserAttribute({
            Name: "custom:Company",
            Value: attrsObj["custom:Company"],
          }),
        ]
      : []),
  ];

  return new Promise((resolve, reject) => {
    userPool.signUp(email, password, attrs, null, (err, data) =>
      err ? reject(err) : resolve(data)
    );
  });
}

// ---------- Verify after sign-up ----------
export function confirmSignUp(email, code) {
  const user = getUser(email);
  return new Promise((resolve, reject) => {
    user.confirmRegistration(code, true, (err, res) =>
      err ? reject(err) : resolve(res)
    );
  });
}

export function resendConfirmationCode(email) {
  const user = getUser(email);
  return new Promise((resolve, reject) => {
    user.resendConfirmationCode((err, res) =>
      err ? reject(err) : resolve(res)
    );
  });
}

// ---------- Username/password sign-in ----------
export function signIn(email, password) {
  if (!userPool) return Promise.reject(new Error("Configure the Cognito user pool and app client before signing in"));
  const user = getUser(email);
  const auth = new AuthenticationDetails({
    Username: email,
    Password: password,
  });

  return new Promise((resolve, reject) => {
    user.authenticateUser(auth, {
      onSuccess: (session) => {
        const token = session.getAccessToken().getJwtToken();
        localStorage.setItem("mg_token", token);
        resolve({ user, session });
      },
      onFailure: reject,
      newPasswordRequired: () => reject(new Error("New password required")),

      totpRequired: (challengeName) => {
        resolve({ user, challengeName });
      },

      mfaRequired: (challengeName) => {
        if (challengeName === "SOFTWARE_TOKEN_MFA") {
          resolve({ user, challengeName });
        } else {
          reject(new Error("Unsupported MFA type: " + challengeName));
        }
      },
    });
  });
}

export function confirmSignInWithTOTP(user, code) {
  return new Promise((resolve, reject) => {
    user.sendMFACode(
      code,
      {
        onSuccess: (session) => resolve({ user, session }),
        onFailure: (err) => reject(err),
      },
      "SOFTWARE_TOKEN_MFA"
    );
  });
}

// ---------- Forgot password ----------
export function forgotPassword(email) {
  const user = getUser(email);
  return new Promise((resolve, reject) => {
    user.forgotPassword({
      onSuccess: resolve, // Code emailed
      onFailure: reject,
      inputVerificationCode: () => resolve(),
    });
  });
}

export function forgotPasswordSubmit(email, code, newPassword) {
  const user = getUser(email);
  return new Promise((resolve, reject) => {
    user.confirmPassword(code, newPassword, {
      onSuccess: resolve,
      onFailure: reject,
    });
  });
}

export function changePassword(oldPassword, newPassword) {
  const current = userPool.getCurrentUser();
  if (!current) return Promise.reject(new Error("User not logged in"));

  return new Promise((resolve, reject) => {
    current.getSession((err, session) => {
      if (err || !session.isValid()) {
        return reject(new Error("Session invalid. Please log in again."));
      }

      current.changePassword(oldPassword, newPassword, (err, result) => {
        if (err) return reject(err);
        resolve(result);
      });
    });
  });
}

export function updateProfileAttributes({ name, company }) {
  const current = userPool.getCurrentUser();
  if (!current) return Promise.reject(new Error("User not logged in"));

  return new Promise((resolve, reject) => {
    current.getSession((err, session) => {
      if (err || !session.isValid()) {
        return reject(new Error("Session invalid. Please log in again."));
      }

      const attributes = [];

      if (name) {
        attributes.push(
          new CognitoUserAttribute({ Name: "name", Value: name })
        );
      }

      if (company) {
        attributes.push(
          new CognitoUserAttribute({
            Name: "custom:Company",
            Value: company,
          })
        );
      }

      if (attributes.length === 0) {
        return resolve("No changes to update.");
      }

      current.updateAttributes(attributes, (err, result) => {
        if (err) return reject(err);
        resolve(result);
      });
    });
  });
}

// ---------- Session / sign out ----------
export function getUserAttributes() {
  if (!userPool) return Promise.reject(new Error("Cognito configuration is required"));
  const current = userPool.getCurrentUser();
  if (!current) return Promise.reject(new Error("User not logged in"));

  return new Promise((resolve, reject) => {
    current.getSession((err, session) => {
      if (err || !session.isValid()) {
        return reject(new Error("Session invalid. Please log in again."));
      }

      current.getUserAttributes((err, attributes) => {
        if (err) return reject(err);

        const attrMap = {};
        for (const attr of attributes) {
          attrMap[attr.getName()] = attr.getValue();
        }
        resolve(attrMap);
      });
    });
  });
}

export function signOut() {
  const current = userPool?.getCurrentUser();
  if (current) {
    localStorage.setItem("pref_mfa", false);
    current.signOut();
  }
}

// ---------- TOTP MFA Setup ----------
export function setupTOTP(user) {
  return new Promise((resolve, reject) => {
    user.associateSoftwareToken({
      associateSecretCode: (secretCode) => {
        resolve(secretCode);
      },
      onFailure: (err) => {
        reject(err);
      },
    });
  });
}

export function verifyTOTP(user, code) {
  return new Promise((resolve, reject) => {
    user.verifySoftwareToken(code, "Authenticator App", {
      onSuccess: (session) => {
        resolve(session);
      },
      onFailure: (err) => {
        reject(err);
      },
    });
  });
}

export function enableTOTP(user) {
  return new Promise((resolve, reject) => {
    user.setUserMfaPreference(
      null,
      { PreferredMfa: true, Enabled: true },
      (err, res) => (err ? reject(err) : resolve(res))
    );
  });
}

export function disableTOTP(user) {
  return new Promise((resolve, reject) => {
    user.setUserMfaPreference(
      null,
      { PreferredMfa: false, Enabled: false },
      (err, res) => (err ? reject(err) : resolve(res))
    );
  });
}

export function getUserMfaPreference(user) {
  return new Promise((resolve, reject) => {
    user.getUserData((err, data) => {
      if (err) return reject(err);
      if (data.PreferredMfaSetting) {
        resolve(data.PreferredMfaSetting);
      } else if (
        data.UserMFASettingList &&
        data.UserMFASettingList.includes("SOFTWARE_TOKEN_MFA")
      ) {
        resolve("SOFTWARE_TOKEN_MFA");
      } else {
        resolve(null);
      }
    });
  });
}

export async function checkMfaPreference() {
  try {
    const session = await getCurrentSession();
    const pref = await getUserMfaPreference(session.user);

    return pref === "SOFTWARE_TOKEN_MFA";
  } catch (error) {
    console.error("Failed to get MFA preference:", error);
  }
}

export async function checkIsAdmin(token) {
  const decoded = jwtDecode(token);
  return decoded["cognito:groups"]?.includes("Admin");
}
